import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { Link } from "react-router-dom";
// import messages from "messages";
// components
import OrchidProAvatar from "components/orchid-pro-avatar";
// import { ReactComponent as OutlineCheckCircleFilled16 } from "assets/svg/outline-check-circle-filled-16.svg";
import AvailabilityTag from "components/orchid-pro/availability-tag/AvailabilityTag";
// utils
import {
  getOrchidProCertificationString,
  getOrchidProTypeStringFromNewArr,
  getOrchidProAbbrevNameAndAllDegreesString,
} from "utils/formatter/orchidPro";
import getClasses from "utils/getClasses";
// styles
import styles from "./styles.module.scss";
// services
import searchOrchidPro from "services/searchOrchidPro";
// constants
import { GENERAL_ROUTES } from "constants/generalRoutes";
import {
  MAX_ITEMS,
  SCROLL_INTERVAL_MS,
  SCROLL_SPEED,
  SEARCH_LIMIT,
} from "./constants";
import { AmplitudePageName, track } from "utils/amplitude";

const COUNT_PER_PAGE = Math.ceil(window.innerWidth / 250) + 1;
const AMPLITUDE_COMPONENT_NAME = "pro-cards-carousel";

interface Props {
  amplitudePageName: AmplitudePageName;
}

/**
 * renders stateless UI for a list of providers
 */
const ProvidersList: React.FC<Props> = ({ amplitudePageName }) => {
  const [{ orchidPros }, setSearchOrchidProResult] = useState<{
    orchidPros: OrchidPro[];
    totalCount: number | undefined;
  }>({
    orchidPros: [],
    totalCount: undefined,
  });

  const [busy, setBusy] = useState(true);

  const [isPlaying, setIsPlaying] = useState(false);
  const getOrchidPro = useCallback(async () => {
    try {
      setBusy(true);
      const promises = Array.from(
        { length: Math.ceil(MAX_ITEMS / SEARCH_LIMIT) },
        (_, i) =>
          searchOrchidPro({
            limit: SEARCH_LIMIT,
            skip: SEARCH_LIMIT * i,
          }).then((res) =>
            setSearchOrchidProResult((s) => ({
              orchidPros: [...s.orchidPros, ...res.results],
              totalCount: res.total_count,
            })),
          ),
      );

      await Promise.allSettled(promises);

      setBusy(false);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    getOrchidPro();
  }, [getOrchidPro]);

  useEffect(() => {
    let timerId: number | undefined = undefined;
    if (isPlaying) {
      if (containerRef.current) {
        // @ts-expect-error setInterval type is incorrect
        timerId = setInterval(() => {
          if (!containerRef.current) {
            return;
          }
          if (
            containerRef.current.scrollLeft +
              containerRef.current.clientWidth +
              SCROLL_SPEED >
            containerRef.current.scrollWidth
          ) {
            containerRef.current.scrollLeft = 0;
          } else {
            containerRef.current.scrollLeft += SCROLL_SPEED;
          }
        }, SCROLL_INTERVAL_MS);
      }
    } else {
      if (timerId) {
        clearInterval(timerId);
      }
    }

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [isPlaying]);
  const [hover, setHover] = useState<string[]>([]);

  useEffect(() => {
    if (!hover.length && !busy) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [hover.length, busy]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className={styles["providers-list"]} ref={containerRef}>
      {orchidPros.map((orchidPro) => (
        <Card
          key={orchidPro.cognito_sub}
          className={styles["providers-list__card"]}
          as={Link}
          to={GENERAL_ROUTES.professionalSearch + "/" + orchidPro.custom_url}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => {
            if (!hover.includes(orchidPro.cognito_sub)) {
              setHover([...hover, orchidPro.cognito_sub]);
            }
          }}
          onMouseOut={() => {
            if (hover.includes(orchidPro.cognito_sub)) {
              const index = hover.indexOf(orchidPro.cognito_sub);
              setHover([...hover.slice(0, index), ...hover.slice(index + 1)]);
            }
          }}
          onClick={() =>
            track(
              amplitudePageName,
              [AMPLITUDE_COMPONENT_NAME, "pro-card"],
              "click",
            )
          }
        >
          <Card.Body className={styles["providers-list__card-body"]}>
            <div className={styles["providers-list__avatar-container"]}>
              <OrchidProAvatar
                orchidPro={orchidPro}
                className={styles["providers-list__avatar"]}
                style={{
                  width: "7.5rem",
                  height: "7.5rem",
                }}
                hideGroup={true}
              />
            </div>

            <div className={styles["providers-list__text"]}>
              <div className={styles["providers-list__title"]}>
                {getOrchidProAbbrevNameAndAllDegreesString(orchidPro)}
              </div>
              {!orchidPro.orchid_pro_types?.length ? null : (
                <p className={styles["providers-list__subtitle"]}>
                  {getOrchidProTypeStringFromNewArr(orchidPro)}
                </p>
              )}
              {!orchidPro.orchid_pro_types?.length ? null : (
                <p className={styles["providers-list__subtitle"]}>
                  {getOrchidProCertificationString(orchidPro)}
                </p>
              )}
            </div>
            {/* <div className={styles["providers-list__tag"]}>
              <OutlineCheckCircleFilled16
                aria-hidden={true}
                className={styles["provider-list__icon"]}
              />
              <div>{messages.orchidVerified}</div>
            </div> */}
            {orchidPro.can_accept_payment && (
              <AvailabilityTag
                orchid_pro_cognito_sub={orchidPro.cognito_sub}
                highlightToday
              />
            )}
          </Card.Body>
        </Card>
      ))}
      {busy &&
        !orchidPros.length &&
        Array.from({ length: COUNT_PER_PAGE }, (_, i) => (
          <Card
            key={i}
            className={getClasses(
              styles["providers-list__card"],
              styles["providers-list__card--loading"],
            )}
          />
        ))}
    </div>
  );
};

export default ProvidersList;
